import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api/axios';
import AdminLayout from '../../layouts/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ImageUploader from '../../components/ImageUploader';

const ProductAdd = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [unitType, setUnitType] = useState('piece');
    const [minOrder, setMinOrder] = useState(1);
    const [maxOrder, setMaxOrder] = useState('');
    const [transportFee, setTransportFee] = useState(0);
    const [baseSku, setBaseSku] = useState(''); // Odoo product.template.default_code
    const [mainImageItems, setMainImageItems] = useState([]);

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [variations, setVariations] = useState([
        { size: '', color: '', sku: '', dealer_price: '', retail_price: '', stock_quantity: 0, use_main_image: false, imageItems: [] }
    ]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await axios.get('/categories');
                setCategories(res.data);
            } catch (error) {
                console.error('Kategoriler yüklenemedi');
            }
        };
        fetchCategories();
    }, []);

    const addCategory = (categoryId) => {
        const id = Number(categoryId);
        if (id && !selectedCategories.includes(id)) {
            setSelectedCategories([...selectedCategories, id]);
        }
    };

    const removeCategory = (categoryId) => {
        setSelectedCategories(selectedCategories.filter(id => id !== categoryId));
    };

    const handleVariationChange = (index, field, value) => {
        const newVariations = [...variations];
        newVariations[index][field] = value;
        setVariations(newVariations);
    };

    const handleVariationImagesChange = (index, newItems) => {
        const newVariations = [...variations];
        newVariations[index].imageItems = newItems;
        setVariations(newVariations);
    };

    const addVariationRow = () => {
        setVariations([...variations, { size: '', color: '', sku: '', dealer_price: '', retail_price: '', stock_quantity: 0, use_main_image: false, imageItems: [] }]);
    };

    const removeVariationRow = (index) => {
        setVariations(variations.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('description', description);
            formData.append('unit_type', unitType);
            formData.append('min_order_limit', minOrder);
            if (maxOrder) formData.append('max_order_limit', maxOrder);
            formData.append('transport_fee', transportFee);
            if (baseSku.trim()) formData.append('base_sku', baseSku.trim());
            formData.append('categoryIds', JSON.stringify(selectedCategories));

            // imageItems React state objesi - JSON'a dahil etme
            const variationsToSend = variations.map(({ imageItems, ...rest }) => rest);
            formData.append('variations', JSON.stringify(variationsToSend));

            // Ana görseller (sıralı)
            mainImageItems.forEach(item => {
                formData.append('images', item.file);
            });

            // Varyasyon görselleri (var_img_{vIndex}_{imgIndex})
            variations.forEach((variant, vi) => {
                if (!variant.use_main_image && variant.imageItems) {
                    variant.imageItems.forEach((item, ji) => {
                        formData.append(`var_img_${vi}_${ji}`, item.file);
                    });
                }
            });

            await axios.post('/products', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(t('messages.product_added'));
            navigate('/admin/products');
        } catch (error) {
            toast.error(error.response?.data?.message || t('messages.product_add_error'));
        }
    };

    return (
        <AdminLayout>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>{t('product_form.add_title')}</h2>
                <button onClick={() => navigate('/admin/products')} className="btn btn-secondary">{t('product_form.back')}</button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* Sol Taraf */}
                    <div className="col-md-8">
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light">{t('product_form.general_info')}</div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.product_name')}</label>
                                    <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label" style={{color: '#7c3aed', fontWeight: 600}}>
                                        🔗 Odoo Stok Kodu (Base SKU)
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="base_sku"
                                        placeholder="Örn: VM-HALI-001 (isteğe bağlı)"
                                        value={baseSku}
                                        onChange={e => setBaseSku(e.target.value)}
                                    />
                                    <small className="text-muted">
                                        Odoo'daki <code>product.template.default_code</code> değeriyle eşleşmelidir.
                                    </small>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.description')}</label>
                                    <ReactQuill theme="snow" value={description} onChange={setDescription} style={{ height: '200px', marginBottom: '50px' }} />
                                </div>
                                <div className="mb-3">
                                    <ImageUploader
                                        items={mainImageItems}
                                        onChange={setMainImageItems}
                                        label={t('product_form.images')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Varyasyonlar */}
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                <span>{t('product_form.variations_title')}</span>
                                <button type="button" className="btn btn-sm btn-primary" onClick={addVariationRow}>{t('product_form.add_variation')}</button>
                            </div>
                            <div className="card-body">
                                {variations.map((variant, index) => (
                                    <div key={index} className="border rounded p-3 mb-3">
                                        {/* Fiyat/Stok Satırı */}
                                        <div className="row g-2 mb-2 align-items-end">
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.size')}</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="100x200"
                                                    value={variant.size} onChange={e => handleVariationChange(index, 'size', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.color')}</label>
                                                <input type="text" className="form-control form-control-sm" placeholder="Antrasit"
                                                    value={variant.color} onChange={e => handleVariationChange(index, 'color', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.dealer_price')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0.00" required
                                                    value={variant.dealer_price} onChange={e => handleVariationChange(index, 'dealer_price', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.sales_price')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0.00" required
                                                    value={variant.retail_price} onChange={e => handleVariationChange(index, 'retail_price', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 col-6">
                                                <label className="form-label small">{t('product_form.stock')}</label>
                                                <input type="number" className="form-control form-control-sm" placeholder="0"
                                                    value={variant.stock_quantity} onChange={e => handleVariationChange(index, 'stock_quantity', e.target.value)} />
                                            </div>
                                            <div className="col-md-2 text-end">
                                                {variations.length > 1 && (
                                                    <button type="button" className="btn btn-sm btn-danger" onClick={() => removeVariationRow(index)}>{t('product_form.delete')}</button>
                                                )}
                                            </div>
                                        </div>
                                        {/* Görsel Satırı */}
                                        <div className="border-top pt-2 mt-1">
                                            <div className="form-check form-switch mb-2">
                                                <input className="form-check-input" type="checkbox"
                                                    checked={variant.use_main_image}
                                                    onChange={e => handleVariationChange(index, 'use_main_image', e.target.checked)}
                                                />
                                                <label className="form-check-label small">{t('product_form.main_image')}</label>
                                            </div>
                                            {!variant.use_main_image && (
                                                <ImageUploader
                                                    items={variant.imageItems || []}
                                                    onChange={(items) => handleVariationImagesChange(index, items)}
                                                    label="Varyasyon Görselleri"
                                                />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Sağ Taraf */}
                    <div className="col-md-4">
                        <div className="card mb-4 shadow-sm">
                            <div className="card-header bg-light">{t('product_form.sales_settings')}</div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.sales_unit')}</label>
                                    <select className="form-select" value={unitType} onChange={e => setUnitType(e.target.value)}>
                                        <option value="piece">{t('common.unit_piece')}</option>
                                        <option value="m2">{t('common.unit_m2')}</option>
                                        <option value="pallet">{t('common.unit_pallet')}</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.min_order')} ({unitType})</label>
                                    <input type="number" className="form-control" value={minOrder} onChange={e => setMinOrder(e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.shipping_fee')}</label>
                                    <input type="number" className="form-control" value={transportFee} onChange={e => setTransportFee(e.target.value)} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('product_form.categories')}</label>
                                    <div className="input-group mb-2">
                                        <select className="form-select" onChange={(e) => addCategory(e.target.value)} value="">
                                            <option value="">{t('product_form.select_category')}</option>
                                            {categories.filter(cat => !selectedCategories.includes(cat.id)).map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                        <button className="btn btn-outline-secondary" type="button" disabled>+</button>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {selectedCategories.map(catId => {
                                            const cat = categories.find(c => c.id === catId);
                                            return cat ? (
                                                <span key={cat.id} className="badge bg-primary d-flex align-items-center p-2">
                                                    {cat.name}
                                                    <button type="button" className="btn-close btn-close-white ms-2" style={{ fontSize: '0.5rem' }} onClick={() => removeCategory(cat.id)}></button>
                                                </span>
                                            ) : null;
                                        })}
                                    </div>
                                    {selectedCategories.length === 0 && <small className="text-muted">{t('product_form.no_category_selected')}</small>}
                                </div>
                                <button type="submit" className="btn btn-success w-100">{t('product_form.save_product')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </AdminLayout>
    );
};

export default ProductAdd;
